/**
 * AppFooter
 *
 * Renders the shared site footer, social links, privacy link, and cookie
 * consent/settings controls for the public app shell.
 * Mounted once from `App.tsx` so it appears below the routed page content.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScreenSize } from '../../contexts/ScreenSizeContext';
import { useStyle } from '../../contexts/StyleContext';
import { updateSessionPreference } from '../../services/authApi';
import {
  CookiePreferences,
  defaultCookiePreferences,
  hasSavedCookiePreferences,
  readCookiePreferences,
  saveCookiePreferences as persistCookiePreferences,
} from '../../services/cookiePreferences';

interface AppFooterProps {
  yearOverride?: number;
}

const socialIconStyle: React.CSSProperties = {
  width: '0.95rem',
  height: '0.95rem',
  flexShrink: 0,
};

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.9" style={socialIconStyle}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
    <circle cx="12" cy="12" r="4.2" />
    <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" style={socialIconStyle}>
    <path d="M21 12c0 2.4-.3 4-.6 4.8a2.8 2.8 0 0 1-1.6 1.6c-.9.3-3.6.6-6.8.6s-5.9-.3-6.8-.6a2.8 2.8 0 0 1-1.6-1.6C3.3 16 3 14.4 3 12s.3-4 .6-4.8a2.8 2.8 0 0 1 1.6-1.6C6.1 5.3 8.8 5 12 5s5.9.3 6.8.6a2.8 2.8 0 0 1 1.6 1.6c.3.8.6 2.4.6 4.8Z" />
    <path d="m10 9 5 3-5 3V9Z" fill="currentColor" stroke="none" />
  </svg>
);

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" style={socialIconStyle}>
    <path d="M14 4c.5 2.1 1.9 3.7 4 4.5v3.2a7.4 7.4 0 0 1-4-1.2v5a4.8 4.8 0 1 1-4.8-4.8c.4 0 .8 0 1.2.1v3.1a2 2 0 1 0 1.1 1.7V4H14Z" />
  </svg>
);

const AppFooter: React.FC<AppFooterProps> = ({ yearOverride }) => {
  const { colorScheme } = useStyle();
  const { isMobile } = useScreenSize();
  const year = yearOverride || new Date().getFullYear();
  const [cookiePreferences, setCookiePreferences] = useState<CookiePreferences>(defaultCookiePreferences());
  const [draftCookiePreferences, setDraftCookiePreferences] = useState<CookiePreferences>(defaultCookiePreferences());
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

  useEffect(() => {
    if (isInIframe) {
      setShowCookieBanner(false);
      return;
    }

    if (!hasSavedCookiePreferences()) {
      setShowCookieBanner(true);
      return;
    }
    const nextPreferences = readCookiePreferences();
    setCookiePreferences(nextPreferences);
    setDraftCookiePreferences(nextPreferences);
  }, [isInIframe]);

  const saveCookiePreferences = (nextPreferences: CookiePreferences) => {
    setCookiePreferences(nextPreferences);
    setDraftCookiePreferences(nextPreferences);
    setShowCookieBanner(false);
    setIsCookieModalOpen(false);
    persistCookiePreferences(nextPreferences);
    void updateSessionPreference(nextPreferences.preferences).catch(() => undefined);
  };

  const handleAcceptAllCookies = () => {
    saveCookiePreferences({
      essential: true,
      preferences: true,
      analytics: true,
      marketing: true,
      updatedAt: new Date().toISOString(),
    });
  };

  const handleEssentialOnlyCookies = () => {
    saveCookiePreferences({
      essential: true,
      preferences: false,
      analytics: false,
      marketing: false,
      updatedAt: new Date().toISOString(),
    });
  };

  const openCookieSettings = () => {
    setDraftCookiePreferences(cookiePreferences);
    setIsCookieModalOpen(true);
  };

  const saveCookieSettings = () => {
    saveCookiePreferences({
      essential: true,
      preferences: draftCookiePreferences.preferences,
      analytics: draftCookiePreferences.analytics,
      marketing: draftCookiePreferences.marketing,
      updatedAt: new Date().toISOString(),
    });
  };

  const socialLinks = useMemo(() => ([
    { label: 'Instagram', href: 'https://www.instagram.com/lovi_book/', icon: <InstagramIcon /> },
    { label: 'YouTube', href: 'https://www.youtube.com/@LoviBook', icon: <YouTubeIcon /> },
  ]), []);

  const footerTextLinkStyle: React.CSSProperties = {
    textDecoration: 'none',
    color: colorScheme.TEXT_SECONDARY,
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontSize: isMobile ? '0.74rem' : '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  };

  return (
    <>
      <div
        style={{
          borderTop: `1px solid ${colorScheme.BORDER}`,
          background: `linear-gradient(180deg, ${colorScheme.BACKGROUND} 0%, ${colorScheme.WHITE} 100%)`,
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
        }}
      >
        <div
          style={{
            maxWidth: '1100px',
            margin: '0 auto',
            padding: isMobile ? '0.85rem 0.9rem 0.75rem' : '1rem 1.5rem 0.85rem',
            display: 'grid',
            gridTemplateColumns: isMobile ? 'auto 1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: isMobile ? '0.7rem' : '1rem',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: isMobile ? '0.45rem' : '0.75rem',
              alignItems: isMobile ? 'flex-start' : 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: '0.45rem',
              }}
            >
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={link.label}
                  title={link.label}
                  style={{
                    textDecoration: 'none',
                    color: colorScheme.TEXT_PRIMARY,
                    background: 'transparent',
                    border: `1px solid ${colorScheme.BORDER}`,
                    borderRadius: '999px',
                    width: '2.1rem',
                    height: '2.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {link.icon}
                </a>
              ))}
              <a
                href="https://www.tiktok.com/@lovibook"
                target="_blank"
                rel="noreferrer"
                aria-label="TikTok"
                title="TikTok"
                style={{
                  textDecoration: 'none',
                  color: colorScheme.TEXT_PRIMARY,
                  background: 'transparent',
                  border: `1px solid ${colorScheme.BORDER}`,
                  borderRadius: '999px',
                  width: '2.1rem',
                  height: '2.1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TikTokIcon />
              </a>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              gap: isMobile ? '0.45rem' : '0.75rem',
              justifyContent: isMobile ? 'flex-start' : 'flex-end',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: isMobile ? '0.5rem 0.75rem' : '0.6rem 0.9rem',
                justifyContent: isMobile ? 'flex-start' : 'flex-end',
                width: '100%',
              }}
            >
              <Link
                to="/contact"
                style={footerTextLinkStyle}
              >
                Contact Us
              </Link>
              <Link
                to="/privacy-policy"
                style={footerTextLinkStyle}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms-of-service"
                style={footerTextLinkStyle}
              >
                Terms of Service
              </Link>
              <button
                type="button"
                onClick={openCookieSettings}
                style={footerTextLinkStyle}
              >
                Cookie Settings
              </button>
            </div>
          </div>
        </div>

        <footer
          style={{
            padding: '1rem 0 2.6rem',
            textAlign: 'center',
            fontSize: '0.6rem',
            color: colorScheme.TEXT_SECONDARY,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          © {year} LoviBook. All rights reserved.
        </footer>
      </div>

      {showCookieBanner ? (
        <div
          style={{
            position: 'fixed',
            left: isMobile ? '0.75rem' : '2rem',
            right: isMobile ? '0.75rem' : 'auto',
            width: isMobile ? 'auto' : '450px',
            bottom: isMobile ? '0.75rem' : '1.25rem',
            background: colorScheme.WHITE,
            border: `0.125rem solid ${colorScheme.PRIMARY}`,
            borderRadius: isMobile ? '1rem' : '1.15rem',
            boxShadow: '0 18px 40px rgba(0,0,0,0.18)',
            padding: isMobile ? '0.85rem' : '1rem 1.1rem',
            zIndex: 1200,
            display: 'grid',
            gap: isMobile ? '0.6rem' : '0.75rem',
          }}
        >
          <div>
            <div style={{ fontSize: '0.92rem', fontWeight: 800, color: colorScheme.PRIMARY, marginBottom: '0.15rem' }}>
              Your cookie settings
            </div>
            <div style={{ fontSize: isMobile ? '0.78rem' : '0.8rem', lineHeight: 1.4, color: colorScheme.TEXT_PRIMARY }}>
              We use essential cookies to keep LoviBook working. {!isMobile && 'You can also choose whether to allow analytics and marketing cookies.'}
            </div>
            {!isMobile && (
              <div style={{ fontSize: '0.8rem', lineHeight: 1.4, color: colorScheme.TEXT_SECONDARY, marginTop: '0.35rem' }}>
                Preference cookies control optional conveniences like keeping you signed in across browser restarts.
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleAcceptAllCookies}
              style={{ background: colorScheme.PRIMARY, color: colorScheme.WHITE, border: 'none', borderRadius: '0.8rem', padding: isMobile ? '0.7rem 0.9rem' : '0.78rem 1rem', fontWeight: 800, cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '0.9rem', flex: isMobile ? 1 : 'auto', minWidth: 0 }}
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={handleEssentialOnlyCookies}
              style={{ background: `${colorScheme.PRIMARY}12`, color: colorScheme.PRIMARY, border: `1.5px solid ${colorScheme.PRIMARY}`, borderRadius: '0.8rem', padding: isMobile ? '0.7rem 0.9rem' : '0.78rem 1rem', fontWeight: 800, cursor: 'pointer', fontSize: isMobile ? '0.85rem' : '0.9rem', flex: isMobile ? 1 : 'auto', minWidth: 0, boxShadow: `0 4px 12px ${colorScheme.PRIMARY}20` }}
            >
              Essential only
            </button>
          </div>
          {isMobile && (
            <button
              type="button"
              onClick={openCookieSettings}
              style={{ background: 'none', color: colorScheme.PRIMARY, border: 'none', padding: '0.35rem 0', fontWeight: 600, cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}
            >
              Learn more about cookies
            </button>
          )}
        </div>
      ) : null}

      {isCookieModalOpen ? (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: 'rgba(0,0,0,0.65)',
            padding: isMobile ? '1rem' : '2rem',
            zIndex: 1250,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              background: colorScheme.WHITE,
              border: `0.25rem solid ${colorScheme.PRIMARY}`,
              borderRadius: isMobile ? '0.75rem' : '1rem',
              maxWidth: isMobile ? '360px' : '38rem',
              width: isMobile ? '90vw' : '100%',
              maxHeight: isMobile ? '60vh' : '80vh',
              margin: '0 auto',
              boxShadow: '0 0.75rem 2rem rgba(0,0,0,0.35)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: isMobile ? '1rem 1rem 0.75rem' : '1.25rem 1.5rem 1rem', borderBottom: `0.0625rem solid ${colorScheme.BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontSize: isMobile ? '1.1rem' : '1.25rem', color: colorScheme.TEXT_PRIMARY }}>
                Cookie settings
              </h4>
              <button
                type="button"
                onClick={() => setIsCookieModalOpen(false)}
                style={{ background: 'transparent', border: 'none', fontSize: '1.75rem', lineHeight: 1, cursor: 'pointer', color: colorScheme.PRIMARY }}
                aria-label="Close cookie settings"
              >
                &times;
              </button>
            </div>
            <div style={{ flex: 1, padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'grid', gap: '1rem' }}>
              <p style={{ margin: 0, color: colorScheme.TEXT_SECONDARY, lineHeight: 1.5 }}>
                Essential cookies stay on so LoviBook can work properly. Preference cookies control optional conveniences like Remember me, while analytics and marketing stay optional.
              </p>

              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <div style={{ border: `0.0625rem solid ${colorScheme.BORDER}`, borderRadius: '0.85rem', padding: '0.9rem 1rem', background: `${colorScheme.PRIMARY}08` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
                    <div>
                      <div style={{ fontWeight: 800, color: colorScheme.PRIMARY }}>Essential cookies</div>
                      <div style={{ fontSize: '0.82rem', color: colorScheme.TEXT_SECONDARY, lineHeight: 1.4 }}>
                        Required for sign-in, navigation, and core booking functionality.
                      </div>
                    </div>
                    <div style={{ fontWeight: 800, color: colorScheme.PRIMARY }}>Always on</div>
                  </div>
                </div>

                {([
                  {
                    key: 'preferences',
                    title: 'Preference cookies',
                    description: 'Allow optional convenience cookies like Remember me so your secure sign-in can stay available across browser restarts.',
                    value: draftCookiePreferences.preferences,
                  },
                  {
                    key: 'analytics',
                    title: 'Analytics cookies',
                    description: 'Help us understand how visitors use the site so we can improve the experience.',
                    value: draftCookiePreferences.analytics,
                  },
                  {
                    key: 'marketing',
                    title: 'Marketing cookies',
                    description: 'Allow advertising cookies and personalized measurement. Google may receive limited cookieless measurement signals while advertising consent is denied on public marketing pages and after confirmed business-setup or subscription milestones.',
                    value: draftCookiePreferences.marketing,
                  },
                ] as Array<{
                  key: 'preferences' | 'analytics' | 'marketing';
                  title: string;
                  description: string;
                  value: boolean;
                }>).map((item) => (
                  <label
                    key={item.key}
                    style={{
                      border: `0.0625rem solid ${colorScheme.BORDER}`,
                      borderRadius: '0.85rem',
                      padding: '0.9rem 1rem',
                      display: 'flex',
                      justifyContent: 'flex-start',
                      gap: '1rem',
                      alignItems: 'flex-start',
                      flexDirection: 'row',
                      cursor: 'pointer',
                      position: 'relative',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={item.value}
                      onChange={(event) => {
                        const nextValue = event.target.checked;
                        setDraftCookiePreferences((current) => ({
                          ...current,
                          [item.key]: nextValue,
                        }));
                      }}
                      style={isMobile ? { 
                        width: '1rem', 
                        height: '1rem', 
                        flexShrink: 0,
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem'
                      } : { width: '1rem', height: '1rem', flexShrink: 0 }}
                    />
                    <div style={isMobile ? { paddingRight: '2rem' } : {}}>
                      <div style={{ fontWeight: 800, color: colorScheme.TEXT_PRIMARY }}>{item.title}</div>
                      <div style={{ fontSize: '0.82rem', color: colorScheme.TEXT_SECONDARY, lineHeight: 1.4 }}>
                        {item.description}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ padding: isMobile ? '0.85rem 1rem 1rem' : '1rem 1.5rem 1.25rem', borderTop: `0.0625rem solid ${colorScheme.BORDER}`, display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                type="button"
                onClick={handleEssentialOnlyCookies}
                style={{ background: `${colorScheme.PRIMARY}12`, color: colorScheme.PRIMARY, border: `1.5px solid ${colorScheme.PRIMARY}`, borderRadius: '0.8rem', padding: '0.78rem 1rem', fontWeight: 800, cursor: 'pointer', width: isMobile ? '100%' : 'auto', boxShadow: `0 4px 12px ${colorScheme.PRIMARY}20` }}
              >
                Essential only
              </button>
              <button
                type="button"
                onClick={saveCookieSettings}
                style={{ background: colorScheme.PRIMARY, color: colorScheme.WHITE, border: 'none', borderRadius: '0.8rem', padding: '0.78rem 1rem', fontWeight: 800, cursor: 'pointer', width: isMobile ? '100%' : 'auto' }}
              >
                Save cookie settings
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AppFooter;
